/** @jsx jsx */
import { colors } from '@apollo/brand';
import { gql } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Card,
  CardBody,
  Link,
  customColors,
  customColorsRaw,
} from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { useMemo } from 'react';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import { StackedTimeSeriesData } from 'src/components/charts/helpers/dataTypes';
import {
  LineChart,
  LineChartStackConfig,
} from 'src/components/charts/LineChart';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { Resolution } from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

export const errorInsightsOperationStatsFragment = gql`
  fragment ErrorInsightsOperationStatsFragment on ServiceQueryStatsRecord {
    metrics {
      totalRequestCount
      requestsWithErrorsCount
    }
    timestamp
  }
`;

export function ErroredRequestsChart({
  data,
  loading,
  resolution,
}: {
  data: StackedTimeSeriesData[];
  loading: boolean;
  resolution: Resolution;
}) {
  const lineChartStacks = useMemo(
    (): LineChartStackConfig<StackedTimeSeriesData>[] => [
      {
        colorGradientTop: `${colors.primitives.blue[300]}3C`,
        colorGradientBottom: `${colors.primitives.blue[300]}00`,
        primaryColor: customColors.flowchart.d,
        cursorColor: customColors.flowchart.d,
        label: 'Total requests',
        data: data ?? [],
        getValue: ({ timestamp, stackCounts }: StackedTimeSeriesData) => ({
          timestamp,
          value: stackCounts[1]?.value ?? 0,
        }),
      },
    ],
    [data],
  );

  const minorLineChartStacks = useMemo(
    (): LineChartStackConfig<StackedTimeSeriesData>[] => [
      {
        primaryColor: customColors.border.error,
        cursorColor: customColors.brand.accent,
        label: 'Errored requests',
        data: data ?? [],
        getValue: ({ timestamp, stackCounts }: StackedTimeSeriesData) => {
          const erroredRequests = stackCounts[0]?.value ?? 0;
          return {
            timestamp,
            value: erroredRequests,
          };
        },
      },
    ],
    [data],
  );

  if (!data) return <div>Loading...</div>;

  return (
    <Card variant="unstyled" className="p-4">
      <CardBody>
        <div className="flex">
          <div className="flex-1">
            <div
              className="mr-2 inline-block size-3 rounded-full"
              css={css`
                background-color: rgb(${customColorsRaw.border.error} / 0.8);
              `}
            />
            <span className="inline-flex items-center">
              Errored requests{' '}
              <Tooltip
                interactive
                label={
                  <p>
                    The total number of requests with errors. To learn more, see
                    the docs on{' '}
                    <Link
                      to={DocsRouter.path('ExtendedErrorConfig')}
                      isExternal
                    >
                      error diagnostics.
                    </Link>
                  </p>
                }
              >
                <IconInfo className="ml-2" />
              </Tooltip>
            </span>
          </div>
          <div className="flex-none">
            <span>Total requests</span>
            <div
              className="ml-2 inline-block size-3 rounded-full"
              css={css`
                background-color: rgb(${customColorsRaw.flowchart.d} / 0.8);
              `}
            />
          </div>
        </div>
        <LineChart
          stacks={lineChartStacks}
          minorStacks={minorLineChartStacks}
          stacked
          strokeWidth={1}
          className={classnames('h-60 pr-5', { 'opacity-40': loading })}
          resolution={resolution}
          tooltipLabelAs={<InsightsLineChartTooltip />}
          hideCursorArrow
        />
      </CardBody>
    </Card>
  );
}
